import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Download, ArrowLeft, File, Image, FileText, Film } from 'lucide-react'
import { getSharedItem, downloadSharedItem, incrementViewCount } from '../services/shareService'
import { getFriendlyFileType } from '../utils/fileHelpers'

interface SharedItem {
  id: string
  hash: string
  name: string
  type: 'file' | 'folder'
  mimeType?: string
  size?: number
  url?: string
  items?: SharedItem[]
}

const SharePage: React.FC = () => {
  const { hash } = useParams<{ hash: string }>()
  const navigate = useNavigate()
  const [item, setItem] = useState<SharedItem | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPath, setCurrentPath] = useState<string>('/')
  const [currentItems, setCurrentItems] = useState<SharedItem[]>([])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return localStorage.getItem('viewMode') as 'grid' | 'list' || 'grid'
  })
  const [selectedFile, setSelectedFile] = useState<SharedItem | null>(null)

  useEffect(() => {
    if (!hash) {
      navigate('/404')
      return
    }

    const fetchSharedItem = async () => {
      try {
        setLoading(true)
        const data = await getSharedItem(hash)
        if (!data) {
          navigate('/404')
          return
        }
        
        setItem(data)
        if (data.type === 'folder' && data.items) {
          setCurrentItems(data.items)
        }
        
        // Increment view count
        await incrementViewCount(hash)
      } catch (error) {
        console.error('Failed to fetch shared item:', error)
        setError('The requested resource could not be found.')
      } finally {
        setLoading(false)
      }
    }

    fetchSharedItem()
  }, [hash, navigate])

  useEffect(() => {
    if (item?.type === 'folder') {
      const pathItems = currentPath === '/' 
        ? item.items || []
        : findItemsByPath(item, currentPath.split('/').filter(Boolean))
      
      setCurrentItems(pathItems)
    }
  }, [currentPath, item])

  // Add ESC key handler to close file preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedFile) {
        setSelectedFile(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedFile])

  const findItemsByPath = (rootItem: SharedItem, pathSegments: string[]): SharedItem[] => {
    if (pathSegments.length === 0) return rootItem.items || []
    
    const [currentSegment, ...remainingSegments] = pathSegments
    const nextFolder = (rootItem.items || []).find(
      item => item.type === 'folder' && item.name === currentSegment
    )
    
    if (!nextFolder) return []
    
    if (remainingSegments.length === 0) {
      return nextFolder.items || []
    }
    
    return findItemsByPath(nextFolder, remainingSegments)
  }

  const handleDownload = async () => {
    if (!item) return
    
    try {
      await downloadSharedItem(item.hash, item.type === 'folder' ? currentPath : undefined)
    } catch (error) {
      console.error('Download failed:', error)
      setError('Failed to download the file.')
    }
  }

  const handleFolderClick = (folderItem: SharedItem) => {
    setCurrentPath(prev => {
      const newPath = prev === '/' ? `/${folderItem.name}` : `${prev}/${folderItem.name}`
      return newPath
    })
  }

  const handleFileClick = (fileItem: SharedItem) => {
    setSelectedFile(fileItem)
  }

  const handleNavigateUp = () => {
    if (currentPath === '/') return
    
    const pathParts = currentPath.split('/')
    pathParts.pop()
    const newPath = pathParts.join('/') || '/'
    setCurrentPath(newPath)
    setSelectedFile(null)
  }

  // Custom folder icon with solid fill
  const FolderIcon = ({ size = 48 }: { size?: number }) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="var(--color-secondary)" 
      stroke="var(--color-secondary)" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <File size={48} />
    
    if (mimeType.startsWith('image/')) {
      return <Image size={48} />
    } else if (mimeType.startsWith('video/')) {
      return <Film size={48} />
    } else if (mimeType.startsWith('text/') || mimeType.includes('pdf')) {
      return <FileText size={48} />
    } else {
      return <File size={48} />
    }
  }

  const renderPreview = () => {
    if (!selectedFile || !selectedFile.url) return null
    
    const mimeType = selectedFile.mimeType || ''
    
    if (mimeType.startsWith('image/')) {
      return (
        <div className="flex justify-center">
          <img 
            src={selectedFile.url} 
            alt={selectedFile.name} 
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
      )
    } else if (mimeType.startsWith('video/')) {
      return (
        <div className="flex justify-center">
          <video 
            controls 
            className="max-w-full max-h-[70vh]"
          >
            <source src={selectedFile.url} type={mimeType} />
            Your browser does not support the video tag.
          </video>
        </div>
      )
    } else if (mimeType.includes('pdf')) {
      return (
        <div className="flex justify-center h-[70vh]">
          <iframe 
            src={`${selectedFile.url}#view=FitH`} 
            className="w-full h-full" 
            title={selectedFile.name}
          />
        </div>
      )
    } else {
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="text-[var(--color-primary)] mb-4">
            {getFileIcon(mimeType)}
          </div>
          <p className="text-lg font-medium mb-2">{selectedFile.name}</p>
          <p className="text-[var(--color-accent)] mb-4">
            {selectedFile.size ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : ''}
          </p>
          <button
            className="btn btn-primary flex items-center"
            onClick={() => window.open(selectedFile.url, '_blank')}
          >
            <Download size={18} className="mr-2" />
            Download File
          </button>
        </div>
      )
    }
  }

  const pathParts = currentPath.split('/').filter(Boolean)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-medium p-8 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-[var(--color-accent)] mb-4">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (!item) return null

  if (item.type === 'file' && item.url) {
    const mimeType = item.mimeType || ''
    
    return (
      <div className="min-h-screen flex flex-col p-4">
        <div className="container mx-auto max-w-4xl bg-white dark:bg-dark-background rounded-lg shadow-custom-medium p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold mb-2">{item.name}</h1>
            <p className="text-[var(--color-accent)]">
              {getFriendlyFileType(mimeType)} • {item.size ? `${(item.size / 1024 / 1024).toFixed(2)} MB` : ''}
            </p>
          </div>
          
          {mimeType.startsWith('image/') ? (
            <div className="flex justify-center mb-6">
              <img 
                src={item.url} 
                alt={item.name} 
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          ) : mimeType.startsWith('video/') ? (
            <div className="flex justify-center mb-6">
              <video 
                controls 
                className="max-w-full max-h-[70vh]"
              >
                <source src={item.url} type={mimeType} />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : mimeType.includes('pdf') ? (
            <div className="flex justify-center h-[70vh] mb-6">
              <iframe 
                src={`${item.url}#view=FitH`} 
                className="w-full h-full" 
                title={item.name}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 mb-6">
              <div className="text-[var(--color-primary)] mb-4">
                {getFileIcon(mimeType)}
              </div>
              <p className="text-lg font-medium mb-2">File Preview Not Available</p>
            </div>
          )}
          
          <div className="flex justify-center">
            <button
              className="btn btn-primary flex items-center"
              onClick={handleDownload}
            >
              <Download size={18} className="mr-2" />
              Download File
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col p-4">
      <div className="container mx-auto">
        <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-medium p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">{item.name}</h1>
            <button
              className="btn btn-primary flex items-center"
              onClick={handleDownload}
            >
              <Download size={18} className="mr-2" />
              Download All
            </button>
          </div>
          
          {selectedFile ? (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  className="flex items-center text-[var(--color-primary)] hover:underline"
                  onClick={() => setSelectedFile(null)}
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back to files
                </button>
                <p className="text-sm text-[var(--color-accent)]">Press ESC to close preview</p>
              </div>
              <div className="bg-[var(--color-background)] p-4 rounded-lg">
                <h2 className="text-xl font-semibold mb-4">{selectedFile.name}</h2>
                {renderPreview()}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2 text-[var(--color-primary)] mb-4">
                <button
                  onClick={() => setCurrentPath('/')}
                  className="hover:underline"
                >
                  Root
                </button>
                {pathParts.map((part, index) => (
                  <React.Fragment key={index}>
                    <span>/</span>
                    <button
                      onClick={() => {
                        const newPath = '/' + pathParts.slice(0, index + 1).join('/')
                        setCurrentPath(newPath)
                      }}
                      className="hover:underline"
                    >
                      {part}
                    </button>
                  </React.Fragment>
                ))}
                {currentPath !== '/' && (
                  <button
                    onClick={handleNavigateUp}
                    className="ml-2 p-1 rounded-full hover:bg-[var(--color-accent)] hover:bg-opacity-20"
                    aria-label="Go up"
                  >
                    <ArrowLeft size={16} />
                  </button>
                )}
              </div>
              
              {currentItems.length === 0 ? (
                <div className="text-center p-8">
                  <p className="text-[var(--color-accent)] mb-2">This folder is empty</p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-[var(--color-background)] rounded-lg p-4 cursor-pointer transition-all duration-300 hover:shadow-custom-medium"
                      onClick={() => item.type === 'folder' ? handleFolderClick(item) : handleFileClick(item)}
                    >
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center h-24 mb-2">
                          {item.type === 'folder' ? (
                            <FolderIcon />
                          ) : (
                            getFileIcon(item.mimeType)
                          )}
                        </div>
                        <p className="font-medium text-center truncate w-full" title={item.name}>
                          {item.name}
                        </p>
                        <p className="text-sm text-[var(--color-accent)]">
                          {item.type === 'file' && item.mimeType 
                            ? getFriendlyFileType(item.mimeType)
                             : item.type === 'folder'
                             ? 'Folder'
                             : ''}
                        </p>
                        <p className="text-sm text-[var(--color-accent)]">
                          {item.type === 'file' && item.size !== undefined
                            ? `${(item.size / 1024).toFixed(2)} KB`
                            : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg">
                  <table className="min-w-full divide-y divide-[var(--color-accent)]">
                    <thead className="bg-[var(--color-background)]">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                          Size
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-dark-background divide-y divide-[var(--color-accent)]">
                      {currentItems.map((item) => (
                        <tr 
                          key={item.id}
                          className="hover:bg-[var(--color-background)] cursor-pointer"
                          onClick={() => item.type === 'folder' ? handleFolderClick(item) : handleFileClick(item)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {item.type === 'folder' ? (
                                <FolderIcon size={20} />
                              ) : (
                                getFileIcon(item.mimeType)
                              )}
                              <span className="font-medium ml-2">{item.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.type === 'folder' ? 'Folder' : getFriendlyFileType(item.mimeType || '')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.type === 'file' && item.size !== undefined
                              ? `${(item.size / 1024).toFixed(2)} KB`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SharePage